import { doc, setDoc, Timestamp, deleteDoc, collection, updateDoc } from 'firebase/firestore';

import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { firestore } from '../firebase';
import Team, { TEAM_STATUSES } from '../types/Team';

export const useTeamMutators = () => {
  const { isGod } = useAuth();
  const { isLeagueAdmin, userState } = useData();

  const addTeam = async (team: Partial<Team>): Promise<Team | null> => {
    if (userState.status !== 'ready' || !userState.userProfile) {
      console.error('User profile not found');
      return null;
    }

    if (!team?.orgId) {
      console.error('Team orgId not found');
      return null;
    }

    if (!team?.leagueId) {
      console.error('Team leagueId not found');
      return null;
    }

    if (!isLeagueAdmin(team.leagueId, team.orgId)) {
      console.error('Permission denied: must be admin of league');
      return null;
    }

    try {
      // Use provided itemId if available, otherwise generate a new one
      const teamRef = team.itemId
        ? doc(firestore, `org/${team.orgId}/team/${team.itemId}`)
        : doc(collection(firestore, `org/${team.orgId}/team`));

      const newTeam = {
        ...team,
        creatorId: userState.userProfile.itemId,
        createTime: Timestamp.now(),
        itemId: teamRef.id,
      };

      await setDoc(teamRef, newTeam);
      return newTeam as Team;
    } catch (error) {
      console.error('Error adding team:', error);
      return null;
    }
  };

  const deleteTeam = async (team: Team, isPermanent = false): Promise<void> => {
    if (!team.orgId || !team.itemId) {
      console.error('Team orgId or itemId not found');
      return;
    }

    if (!isLeagueAdmin(team.leagueId, team.orgId)) {
      console.error('Permission denied. User:', userState.userProfile?.itemId, 'Team:', team.itemId);
      return;
    }

    try {
      const teamRef = doc(firestore, `org/${team.orgId}/team/${team.itemId}`);
      if (isPermanent) {
        console.log('Deleting team:', team.itemId);
        await deleteDoc(teamRef);
      } else {
        console.log('Soft deleting team:', team.itemId);
        await updateDoc(teamRef, { status: TEAM_STATUSES.DELETED });
      }
    } catch (error) {
      console.error('Error deleting team:', error);
    }
  };

  const updateTeam = async (team: Partial<Team>) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { orgId, itemId, leagueId, orgName, leagueName, ...mutableUpdates } = team;

      if (!orgId || !itemId || !leagueId) {
        console.error('Missing required fields:', { orgId, itemId, leagueId });
        return;
      }

      if (!isLeagueAdmin(leagueId, orgId)) {
        console.error('Permission denied: must be admin of league');
        return;
      }

      const teamRef = doc(firestore, `org/${orgId}/team/${itemId}`);
      await updateDoc(teamRef, mutableUpdates);
    } catch (error) {
      console.error('Error updating team:', error);
    }
  };

  const archiveTeam = async (team: Team): Promise<void> => {
    if (!team.orgId || !team.itemId || !team.leagueId) {
      console.error('Team orgId, itemId, or leagueId not found');
      return;
    }

    if (!isLeagueAdmin(team.leagueId, team.orgId)) {
      console.error('Permission denied: must be admin of league');
      return;
    }

    try {
      const teamRef = doc(firestore, `org/${team.orgId}/team/${team.itemId}`);
      await updateDoc(teamRef, { status: TEAM_STATUSES.ARCHIVED });
    } catch (error) {
      console.error('Error archiving team:', error);
    }
  };

  const restoreTeam = async (team: Team): Promise<void> => {
    if (!team.orgId || !team.itemId || !team.leagueId) {
      console.error('Team orgId, itemId, or leagueId not found');
      return;
    }

    if (!isGod && !isLeagueAdmin(team.leagueId, team.orgId)) {
      console.error('Permission denied: must be god or admin of league');
      return;
    }

    try {
      const teamRef = doc(firestore, `org/${team.orgId}/team/${team.itemId}`);
      await updateDoc(teamRef, { status: TEAM_STATUSES.ACTIVE });
    } catch (error) {
      console.error('Error restoring team:', error);
    }
  };

  return {
    addTeam,
    archiveTeam,
    deleteTeam,
    restoreTeam,
    updateTeam,
  };
};
